import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Truck, Stethoscope, ShoppingBag, CalendarClock, ArrowUpRight } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const productIcons = [Truck, Stethoscope, ShoppingBag, CalendarClock];
const productColors = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-rose-600',
  'from-violet-500 to-purple-600',
];
const productLinks = [
  'https://agrosjet.com',
  'https://agrosvet.app',
  null, // AgrosPet - no link
  'https://shiftjet.app',
];

export const ProductsSection = () => {
  const { language } = useLanguage();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section 
      id="products"
      ref={ref}
      className="py-24 md:py-32 bg-slate-50 dark:bg-slate-950 theme-transition"
      data-testid="products-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
          <h2 className="section-title mb-4" data-testid="products-title">
            {translations.products.title[language]}
          </h2>
          <p className="section-subtitle mx-auto">
            {translations.products.subtitle[language]}
          </p>
        </div>

        {/* Products Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {translations.products.items.map((product, index) => {
            const Icon = productIcons[index];
            const gradient = productColors[index];
            const link = productLinks[index];
            
            const CardWrapper = ({ children }) => {
              if (link) {
                return (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-[0_4px_20px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:hover:shadow-[0_8px_30px_rgb(0,0,0,0.4)] cursor-pointer block ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`product-card-${product.name.toLowerCase()}`}
                  >
                    {children}
                  </a>
                );
              }
              return (
                <div
                  className={`group relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-[0_4px_20px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:hover:shadow-[0_8px_30px_rgb(0,0,0,0.4)] ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`product-card-${product.name.toLowerCase()}`}
                >
                  {children}
                </div>
              );
            };

            return (
              <CardWrapper key={index}>
                {/* Gradient Header */}
                <div className={`h-32 bg-gradient-to-br ${gradient} relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10" />
                  <div className="absolute bottom-4 left-6">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                  </div>
                  {link && (
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <ArrowUpRight className="w-4 h-4 text-white" strokeWidth={1.5} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      {product.name}
                    </h3>
                    {link && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                        {language === 'tr' ? 'Aktif' : 'Live'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 mb-3">
                    {product.tagline[language]}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    {product.description[language]}
                  </p>
                </div>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};
